import { fetchJson } from "../lib/api";

export type ArticleInsight = {
  article_group_key: string;
  primary_article_code: string | null;
  manufacturer_id: string | null;
  manufacturer_name?: string | null;
  brand_name: string | null;
  product_family: string | null;
  resolution_confidence: string | null;
  total_qty: string | number | null;
  total_spend: string | number | null;
  distinct_projects_count: number | null;
  distinct_suppliers_count: number | null;
  distinct_manufacturers_count: number | null;
  last_order_doc_date: string | null;
  last_invoice_doc_id: string | null;
  last_invoice_doc_date: string | null;
  last_net_unit_price: string | number | null;
  last_project_key: string | null;
  last_online_user_email: string | null;
  worst_net_unit_price: string | number | null;
  worst_doc_date: string | null;
  worst_project_key: string | null;
  worst_invoice_doc_id: string | null;
  worst_online_user_email: string | null;
  best_active_net_unit_price: string | number | null;
  best_active_project_key: string | null;
  best_active_doc_date: string | null;
  avg_discount_pct: string | number | null;
  weighted_avg_discount_pct: string | number | null;
  has_manufacturer_conflict: boolean | null;
  supplier_list: string | null;
  description_example: string | null;
  alias_codes?: string[];
  invoice_document_count_12m: number;
  last_invoice_doc_date_12m: string | null;
  qty_ytd: string | number | null;
  qty_ytd_uom: string | null;
  qty_ytd_status: ArticleQtyYtdStatus;
  // W1: ABC/rank fields from article_spend_rank_v.
  spend_rank: number | null;
  abc_class: string | null;
  cumulative_spend_share: string | number | null;
  priced_line_count: string | number | null;
  document_count: string | number | null;
  net_price_spread_pct: string | number | null;
  min_net_unit_price: string | number | null;
  // Best price 12M row reference.
  best_12m_doc_date: string | null;
  best_12m_project_key: string | null;
  best_12m_invoice_doc_id: string | null;
  // W2: windowed extrema (last 12/24 months) from article_price_extrema_v.
  min_net_unit_price_12m: string | number | null;
  max_net_unit_price_12m: string | number | null;
  net_price_spread_pct_12m: string | number | null;
  priced_line_count_12m: string | number | null;
  min_net_unit_price_24m: string | number | null;
  max_net_unit_price_24m: string | number | null;
  net_price_spread_pct_24m: string | number | null;
  priced_line_count_24m: string | number | null;
  has_pdf_evidence?: boolean | null;
  has_ugl_evidence?: boolean | null;
  has_datanorm_evidence?: boolean | null;
  has_identity_evidence?: boolean | null;
  has_review_evidence?: boolean | null;
  /** Artikelliste: Rechnungen im gewählten Zeitraum (ohne Zeitraum = 12 Monate). */
  invoice_document_count_window?: number;
};

export type ArticleSortMode =
  | "spend"
  | "frequency"
  | "relevance"
  | "spread"
  | "spread_12m"
  | "spread_24m"
  | "last_order";

export type ArticleListParams = {
  q?: string;
  limit?: number;
  offset?: number;
  withTotal?: boolean;
  sort?: ArticleSortMode;
  manufacturerId?: string;
  manufacturerIds?: string[];
  productFamily?: string;
  abcClass?: "A" | "B" | "C";
  wholesaleSupplier?: string;
  wholesaleSuppliers?: string[];
  /** Großhändler der Rechnung: bei wem gekauft (nicht Katalogdaten). */
  suppliers?: string[];
  costCenters?: string[];
  boughtWithinMonths?: ArticleBoughtWithin;
};

export const ARTICLE_BOUGHT_WITHIN = [3, 6, 12] as const;
export type ArticleBoughtWithin = (typeof ARTICLE_BOUGHT_WITHIN)[number];

export type ArticleListResponse = {
  total: number | null;
  limit: number;
  offset: number;
  activity_period: ArticleActivityPeriod;
  items: ArticleInsight[];
};

export type ArticleQtyYtdStatus =
  | "available"
  | "mixed"
  | "unit_missing"
  | "unavailable";

export type ArticleMatchKind =
  | "exact_code"
  | "exact_alias"
  | "code_prefix"
  | "alias_prefix"
  | "code_contains"
  | "alias_contains"
  | "product_family"
  | "description";

export type ArticleActivityPeriod = {
  as_of_date: string;
  time_zone: string;
  rolling_12m_from: string;
  ytd_from: string;
};

export type PriceHistoryRow = {
  /** Live accounting reference; absent for non-document evidence and older demo data. */
  external_number?: string | null;
  doc_date: string | null;
  doc_type: string | null;
  doc_number: string | null;
  line_pos: string | number | null;
  project_key: string | null;
  project_exists: boolean | null;
  supplier: string | null;
  qty: string | number | null;
  uom: string | null;
  list_unit_price: string | number | null;
  discount_pct: string | number | null;
  net_unit_price: string | number | null;
  effective_unit_price: string | number | null;
  line_net_amount: string | number | null;
  baseline_kind: string | null;
  result: string | null;
  description: string | null;
  page_no: number | null;
  invoice_doc_id: string | null;
  online_user_email: string | null;
  online_user_name: string | null;
  delivery_address_key: string | null;
  delivery_address_display: string | null;
  delivery_is_warehouse: boolean | null;
  source_kind?: string | null;
  wholesale_file_id?: string | null;
  wholesale_source_record_row?: string | number | null;
  wholesale_source_file?: string | null;
  wholesale_raw_record?: string | null;
  /** Comparison price and deviation of the invoice line, as in the Beleg detail. */
  canonical_baseline_price?: string | number | null;
  canonical_delta_pct?: string | number | null;
  is_price_anomaly?: boolean | null;
};

export type PriceHistoryResponse = {
  article_group_key: string;
  total: number;
  limit: number;
  offset: number;
  items: PriceHistoryRow[];
};

export function articleQueryKey(articleGroupKey: string) {
  return ["article", articleGroupKey] as const;
}

export function articlePriceHistoryQueryKey(articleGroupKey: string) {
  return ["article", articleGroupKey, "price-history", "delivery-v1"] as const;
}

export function articleWholesaleQueryKey(
  articleGroupKey: string,
  supplier: string | null,
) {
  return ["article", articleGroupKey, "wholesale", "v1", supplier ?? "all"] as const;
}

export function articleIdentityQueryKey(articleGroupKey: string) {
  return ["article", articleGroupKey, "identity", "v1"] as const;
}

export function articleSetsQueryKey(articleGroupKey: string) {
  return ["article", articleGroupKey, "sets", "v1"] as const;
}

export function articleSourceCoverageQueryKey(articleGroupKey: string) {
  return ["article", articleGroupKey, "source-coverage", "v1"] as const;
}

export type ArticleSuggestion = {
  article_group_key: string;
  primary_article_code: string | null;
  product_family: string | null;
  brand_name: string | null;
  last_net_unit_price: string | number | null;
  total_spend: string | number | null;
  description_example: string | null;
  invoice_document_count_12m: number;
  last_invoice_doc_date_12m: string | null;
  qty_ytd: string | number | null;
  qty_ytd_uom: string | null;
  qty_ytd_status: ArticleQtyYtdStatus;
  match_kind: ArticleMatchKind | null;
};

export type ArticleSuggestResponse = {
  limit: number;
  activity_period: ArticleActivityPeriod;
  items: ArticleSuggestion[];
};

export async function suggestArticles(
  query: string,
  limit = 10,
): Promise<ArticleSuggestResponse> {
  const params = new URLSearchParams();
  if (query.trim()) {
    params.set("q", query.trim());
  }
  params.set("limit", String(limit));
  return fetchJson<ArticleSuggestResponse>(
    `/api/articles/suggest?${params.toString()}`,
  );
}

export async function searchArticles(
  query: string,
  limit = 10,
): Promise<ArticleListResponse> {
  const params = new URLSearchParams();
  if (query.trim()) {
    params.set("q", query.trim());
    params.set("sort", "relevance");
  }
  params.set("limit", String(limit));
  return fetchJson<ArticleListResponse>(`/api/articles?${params.toString()}`);
}

export async function listArticles(
  args: ArticleListParams = {},
): Promise<ArticleListResponse> {
  const params = new URLSearchParams();
  if (args.q && args.q.trim()) params.set("q", args.q.trim());
  params.set("limit", String(args.limit ?? 50));
  params.set("offset", String(args.offset ?? 0));
  if (args.withTotal) params.set("with_total", "true");
  if (args.sort) params.set("sort", args.sort);
  if (args.manufacturerIds?.length) {
    for (const id of args.manufacturerIds) params.append("manufacturer_id", id);
  } else if (args.manufacturerId) {
    params.set("manufacturer_id", args.manufacturerId);
  }
  if (args.productFamily) params.set("product_family", args.productFamily);
  if (args.abcClass) params.set("abc_class", args.abcClass);
  if (args.wholesaleSuppliers?.length) {
    for (const supplier of args.wholesaleSuppliers) {
      params.append("wholesale_supplier", supplier);
    }
  } else if (args.wholesaleSupplier) {
    params.set("wholesale_supplier", args.wholesaleSupplier);
  }
  for (const supplier of args.suppliers ?? []) params.append("supplier", supplier);
  for (const key of args.costCenters ?? []) params.append("cost_center", key);
  if (args.boughtWithinMonths) params.set("bought_within_months", String(args.boughtWithinMonths));
  return fetchJson<ArticleListResponse>(`/api/articles?${params.toString()}`);
}

export type ArticleFilterOptions = {
  manufacturers: { id: string; name: string | null; article_count: number }[];
  suppliers: { supplier: string; article_count: number }[];
  /** Zuletzt bebuchte zuerst; mit `costCenterQ` über alle Kostenstellen gesucht. */
  cost_centers: { type: string; key: string; label: string | null; last_date: string | null; article_count: number }[];
  cost_center_total: number;
  /** Letzter Einkauf im Datenbestand, ungefiltert. */
  last_purchase_date: string | null;
};

export async function fetchArticleFilterOptions(args: {
  q?: string;
  boughtWithinMonths?: ArticleBoughtWithin;
  costCenterQ?: string;
  selectedCostCenters?: string[];
} = {}): Promise<ArticleFilterOptions> {
  const params = new URLSearchParams();
  if (args.q?.trim()) params.set("q", args.q.trim());
  if (args.boughtWithinMonths) params.set("bought_within_months", String(args.boughtWithinMonths));
  if (args.costCenterQ?.trim()) params.set("cost_center_q", args.costCenterQ.trim());
  for (const key of args.selectedCostCenters ?? []) params.append("cost_center", key);
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return fetchJson<ArticleFilterOptions>(`/api/articles/filter-options${suffix}`);
}

export type ArticleWholesaleSuppliersResponse = {
  items: string[];
};

export async function fetchArticleWholesaleSuppliers(): Promise<ArticleWholesaleSuppliersResponse> {
  return fetchJson<ArticleWholesaleSuppliersResponse>(
    `/api/articles/wholesale-suppliers`,
  );
}

export type ArticleWholesaleSupplierRollup = {
  supplier: string;
  article_count: number;
  total_spend: string | number | null;
  spend_share: string | number | null;
};

export type ArticleWholesaleSupplierRollupResponse = {
  items: ArticleWholesaleSupplierRollup[];
};

export type ArticleOverviewSummaryParams = {
  q?: string;
  manufacturerIds?: string[];
  abcClass?: "A" | "B" | "C";
  wholesaleSuppliers?: string[];
};

function appendOverviewSummaryParams(
  params: URLSearchParams,
  args: ArticleOverviewSummaryParams,
) {
  if (args.q && args.q.trim()) params.set("q", args.q.trim());
  if (args.abcClass) params.set("abc_class", args.abcClass);
  if (args.manufacturerIds?.length) {
    for (const id of args.manufacturerIds) params.append("manufacturer_id", id);
  }
  if (args.wholesaleSuppliers?.length) {
    for (const supplier of args.wholesaleSuppliers) {
      params.append("wholesale_supplier", supplier);
    }
  }
}

export async function fetchArticleWholesaleSupplierRollup(
  args: ArticleOverviewSummaryParams = {},
): Promise<ArticleWholesaleSupplierRollupResponse> {
  const params = new URLSearchParams();
  appendOverviewSummaryParams(params, args);
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return fetchJson<ArticleWholesaleSupplierRollupResponse>(
    `/api/articles/wholesale-supplier-rollup${suffix}`,
  );
}

export type AbcSummaryItem = {
  abc_class: string;
  article_count: number;
  total_spend: string | number | null;
  spend_share: string | number | null;
  article_share: string | number | null;
};

export type AbcSummaryResponse = {
  items: AbcSummaryItem[];
};

export async function fetchAbcSummary(
  args: ArticleOverviewSummaryParams = {},
): Promise<AbcSummaryResponse> {
  const params = new URLSearchParams();
  appendOverviewSummaryParams(params, args);
  const suffix = params.toString() ? `?${params.toString()}` : "";
  return fetchJson<AbcSummaryResponse>(`/api/articles/abc-summary${suffix}`);
}

export type ArticleAlternativesResponse = {
  article_group_key: string;
  manufacturer_id: string | null;
  product_family: string | null;
  items: Array<{
    article_group_key: string;
    primary_article_code: string | null;
    brand_name: string | null;
    product_family: string | null;
    description_example: string | null;
    total_spend: string | number | null;
    total_qty: string | number | null;
    last_net_unit_price: string | number | null;
    best_active_net_unit_price: string | number | null;
    worst_net_unit_price: string | number | null;
    abc_class: string | null;
  }>;
};

export type ArticleQtyMonthlyResponse = {
  article_group_key: string;
  months: number;
  items: Array<{
    month_start: string;
    total_qty: string | number | null;
    total_spend: string | number | null;
    priced_line_count: string | number | null;
    project_count: string | number | null;
    weighted_avg_net_unit_price: string | number | null;
  }>;
};

export type ArticleDeliveryBreakdownResponse = {
  article_group_key: string;
  items: Array<{
    delivery_address_key: string | null;
    delivery_address_display: string | null;
    delivery_is_warehouse: boolean | null;
    line_count: string | number | null;
    total_qty: string | number | null;
    total_spend: string | number | null;
    avg_net_unit_price: string | number | null;
    min_net_unit_price: string | number | null;
    max_net_unit_price: string | number | null;
  }>;
};

export type ArticleTopUsersResponse = {
  article_group_key: string;
  items: Array<{
    online_user_email: string | null;
    online_user_name: string | null;
    line_count: string | number | null;
    total_qty: string | number | null;
    total_spend: string | number | null;
    avg_net_unit_price: string | number | null;
    last_doc_date: string | null;
  }>;
};

export async function fetchArticleAlternatives(
  articleGroupKey: string,
  limit = 6,
): Promise<ArticleAlternativesResponse> {
  return fetchJson<ArticleAlternativesResponse>(
    `/api/articles/${encodeURIComponent(articleGroupKey)}/alternatives?limit=${limit}`,
  );
}

export async function fetchArticleQtyMonthly(
  articleGroupKey: string,
  months = 18,
): Promise<ArticleQtyMonthlyResponse> {
  return fetchJson<ArticleQtyMonthlyResponse>(
    `/api/articles/${encodeURIComponent(articleGroupKey)}/qty-monthly?months=${months}`,
  );
}

export async function fetchArticleDeliveryBreakdown(
  articleGroupKey: string,
): Promise<ArticleDeliveryBreakdownResponse> {
  return fetchJson<ArticleDeliveryBreakdownResponse>(
    `/api/articles/${encodeURIComponent(articleGroupKey)}/delivery-breakdown`,
  );
}

export async function fetchArticleTopUsers(
  articleGroupKey: string,
  limit = 5,
): Promise<ArticleTopUsersResponse> {
  return fetchJson<ArticleTopUsersResponse>(
    `/api/articles/${encodeURIComponent(articleGroupKey)}/top-users?limit=${limit}`,
  );
}

export async function fetchArticle(
  articleGroupKey: string,
): Promise<ArticleInsight> {
  return fetchJson<ArticleInsight>(
    `/api/articles/${encodeURIComponent(articleGroupKey)}`,
  );
}

export type ArticleCatalog = {
  article_group_key: string;
  primary_article_code: string | null;
  manufacturer_name: string | null;
  ean: string | null;
  datanorm_manufacturer_article_code: string | null;
  datanorm_list_price: string | number | null;
  datanorm_currency: string | null;
  datanorm_uom: string | null;
  warengruppe: string | null;
  warengruppe_name: string | null;
  warengruppe_level: string | number | null;
  warengruppe_parent_code: string | null;
  warengruppe_root_code: string | null;
  warengruppe_breadcrumb: string | null;
  datanorm_discount_group: string | null;
  datanorm_supplier: string | null;
  datanorm_valid_from: string | null;
  metadata_source: "datanorm" | "pdf" | "both";
};

export type ArticleWholesaleListPrice = {
  article_group_key: string;
  article_code: string | null;
  description: string | null;
  supplier: string | null;
  list_unit_price: string | number | null;
  discount_pct: string | number | null;
  net_unit_price: string | number | null;
  currency: string | null;
  observed_date: string | null;
  source_kind: "datanorm_price" | "datanorm_article";
  file_id: string | null;
  source_record_row: string | number | null;
  source_file: string | null;
  raw_record: string | null;
};

export type ArticleWholesaleEvidence = {
  source_kind: "ugl_rechnung" | "ugl_vorgang";
  supplier: string | null;
  doc_type: string | null;
  doc_date: string | null;
  doc_number: string | null;
  line_pos: string | number | null;
  qty: string | number | null;
  uom: string | null;
  list_unit_price: string | number | null;
  discount_pct: string | number | null;
  net_unit_price: string | number | null;
  line_net_amount: string | number | null;
  description: string | null;
  wholesale_file_id: string | null;
  wholesale_source_record_row: string | number | null;
  wholesale_source_file: string | null;
  wholesale_raw_record: string | null;
};

export type ArticleWholesaleSupplierSpend = {
  supplier: string;
  line_count: string | number | null;
  total_qty: string | number | null;
  total_spend: string | number | null;
  last_doc_date: string | null;
  has_list_price: boolean | null;
};

export type ArticleWholesaleResponse = {
  article_group_key: string;
  supplier_filter: string | null;
  available_suppliers: string[];
  supplier_rollup: ArticleWholesaleSupplierSpend[];
  list_price: ArticleWholesaleListPrice | null;
  evidence: ArticleWholesaleEvidence[];
  evidence_total: number;
  evidence_offset?: number;
};

export type ArticleProductIdentity = {
  supplier: string | null;
  article_group_key: string;
  product_identity_key: string;
  identity_source: "ean" | "manufacturer_article_code" | "supplier_article";
  identity_confidence: "high" | "medium" | "low";
  ean: string | null;
  manufacturer_name: string | null;
  manufacturer_article_code: string | null;
};

export type ArticleProductIdentityEquivalent = ArticleProductIdentity & {
  primary_article_code: string | null;
  datanorm_list_price: string | number | null;
  datanorm_currency: string | null;
  latest_transaction_price: string | number | null;
  min_transaction_price: string | number | null;
  avg_transaction_price: string | number | null;
  transaction_count: string | number | null;
  supplier_count: string | number | null;
  is_current_article: boolean | null;
};

export type ArticleProductIdentityResponse = {
  article_group_key: string;
  identities: ArticleProductIdentity[];
  equivalents: ArticleProductIdentityEquivalent[];
};

export type ArticleDatanormSetMember = {
  supplier: string | null;
  set_group_key: string | null;
  set_code: string | null;
  position: string | null;
  member_group_key: string | null;
  member_article_code: string | null;
  member_qty: string | number | null;
  description: string | null;
  set_list_price: string | number | null;
  member_list_price: string | number | null;
  currency: string | null;
  raw_record: string | null;
  source_file: string | null;
  source_record_row: string | number | null;
};

export type WholesaleSetPriceQa = {
  supplier: string | null;
  set_group_key: string | null;
  set_code: string | null;
  member_count: string | number | null;
  priced_member_count: string | number | null;
  missing_member_price_count: string | number | null;
  member_sum_list_price: string | number | null;
  set_list_price: string | number | null;
  currency: string | null;
  absolute_delta: string | number | null;
  relative_delta: string | number | null;
  qa_status:
    | "ok"
    | "mismatch"
    | "missing_set_price"
    | "missing_members"
    | "missing_member_prices";
};

export type ArticleSetsResponse = {
  article_group_key: string;
  member_of: ArticleDatanormSetMember[];
  contains_members: ArticleDatanormSetMember[];
  qa: WholesaleSetPriceQa[];
};

export type ArticleCoverageStatus =
  | "complete"
  | "review_needed"
  | "source_gap"
  | "unsupported"
  | "unknown";

export type ArticleSupplierSourceCoverage = {
  supplier: string | null;
  has_wholesale_data: boolean | null;
  has_datanorm: boolean | null;
  has_ugl: boolean | null;
  match_kind: string | null;
  match_rank: string | number | null;
  match_reason: string | null;
  matched_wholesale_article_count: string | number | null;
  observation_count: string | number | null;
  priced_observation_count: string | number | null;
  datanorm_observation_count: string | number | null;
  ugl_observation_count: string | number | null;
  ugl_line_spend: string | number | null;
  first_observed_date: string | null;
  last_observed_date: string | null;
  source_kind_list: string | null;
};

export type ArticleSourceCoverage = {
  customer_id: string | null;
  domain: "article" | string;
  entity_key: string;
  entity_type: "article" | string;
  article_group_key: string;
  display_label: string | null;
  primary_article_code: string | null;
  manufacturer_name: string | null;
  total_spend: string | number | null;
  coverage_status: ArticleCoverageStatus | string;
  gap_codes: string | null;
  unsupported_source_codes: string | null;
  confidence_label: string | null;
  has_pdf_evidence: boolean | null;
  has_ugl_evidence: boolean | null;
  has_datanorm_evidence: boolean | null;
  has_datanorm_catalog: boolean | null;
  has_datanorm_set: boolean | null;
  has_identity_evidence: boolean | null;
  has_review_evidence: boolean | null;
  has_manual_evidence: boolean | null;
  pdf_document_count: string | number | null;
  pdf_line_count: string | number | null;
  ugl_document_count: string | number | null;
  ugl_line_count: string | number | null;
  datanorm_catalog_count: string | number | null;
  datanorm_set_count: string | number | null;
  identity_count: string | number | null;
  weak_identity_count: string | number | null;
  review_candidate_count: string | number | null;
  manual_decision_count: string | number | null;
  supplier_count: string | number | null;
  suppliers_with_data: string | null;
  suppliers_with_datanorm: string | null;
  suppliers_with_ugl: string | null;
  total_observation_count: string | number | null;
  total_ugl_observation_count: string | number | null;
  last_observed_date: string | null;
  supplier_coverage: ArticleSupplierSourceCoverage[];
};

export function articleCatalogQueryKey(articleGroupKey: string) {
  return ["article", articleGroupKey, "catalog"] as const;
}

export async function fetchArticleCatalog(
  articleGroupKey: string,
): Promise<ArticleCatalog> {
  return fetchJson<ArticleCatalog>(
    `/api/articles/${encodeURIComponent(articleGroupKey)}/catalog`,
  );
}

export async function fetchArticleWholesale(
  articleGroupKey: string,
  evidenceLimit = 200,
  supplier: string | null = null,
  evidenceOffset = 0,
): Promise<ArticleWholesaleResponse> {
  const params = new URLSearchParams({
    evidence_limit: String(evidenceLimit),
    evidence_offset: String(evidenceOffset),
  });
  if (supplier) {
    params.set("supplier", supplier);
  }
  return fetchJson<ArticleWholesaleResponse>(
    `/api/articles/${encodeURIComponent(articleGroupKey)}/wholesale?${params.toString()}`,
  );
}

export async function fetchCompleteArticleWholesale(
  articleGroupKey: string,
  supplier: string | null = null,
): Promise<ArticleWholesaleResponse> {
  const pageSize = 2000;
  const first = await fetchArticleWholesale(articleGroupKey, pageSize, supplier);
  const evidence = [...first.evidence];
  while (evidence.length < first.evidence_total) {
    const page = await fetchArticleWholesale(articleGroupKey, pageSize, supplier, evidence.length);
    if (!page.evidence.length) throw new Error("UGL-Nachweise konnten nicht vollständig geladen werden.");
    evidence.push(...page.evidence);
  }
  return { ...first, evidence };
}

export async function fetchArticleIdentity(
  articleGroupKey: string,
): Promise<ArticleProductIdentityResponse> {
  return fetchJson<ArticleProductIdentityResponse>(
    `/api/articles/${encodeURIComponent(articleGroupKey)}/identity`,
  );
}

export async function fetchArticleSets(
  articleGroupKey: string,
): Promise<ArticleSetsResponse> {
  return fetchJson<ArticleSetsResponse>(
    `/api/articles/${encodeURIComponent(articleGroupKey)}/sets`,
  );
}

export async function fetchArticleSourceCoverage(
  articleGroupKey: string,
): Promise<ArticleSourceCoverage> {
  return fetchJson<ArticleSourceCoverage>(
    `/api/articles/${encodeURIComponent(articleGroupKey)}/source-coverage`,
  );
}

export async function fetchPriceHistory(
  articleGroupKey: string,
  limit = 200,
  offset = 0,
): Promise<PriceHistoryResponse> {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  return fetchJson<PriceHistoryResponse>(
    `/api/articles/${encodeURIComponent(articleGroupKey)}/price-history?${params.toString()}`,
  );
}

export async function fetchCompletePriceHistory(
  articleGroupKey: string,
): Promise<PriceHistoryResponse> {
  const pageSize = 2000;
  const firstPage = await fetchPriceHistory(articleGroupKey, pageSize, 0);
  const items = [...firstPage.items];

  while (items.length < firstPage.total) {
    const page = await fetchPriceHistory(articleGroupKey, pageSize, items.length);
    if (page.items.length === 0) throw new Error("Belegpositionen konnten nicht vollständig geladen werden.");
    items.push(...page.items);
  }

  return {
    ...firstPage,
    limit: items.length,
    offset: 0,
    items,
  };
}

export function articleCreditsQueryKey(articleGroupKey: string) {
  return ["article", articleGroupKey, "credits"] as const;
}

// Gutschrift (credit-note) lines for an article — shown in the orders/evidence
// list only. Kept separate from price-history so price KPIs/charts stay
// rechnung-only. line_net_amount is the signed (negative) credit amount.
export async function fetchArticleCredits(
  articleGroupKey: string,
  limit = 200,
  offset = 0,
): Promise<PriceHistoryResponse> {
  const params = new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  });
  return fetchJson<PriceHistoryResponse>(
    `/api/articles/${encodeURIComponent(articleGroupKey)}/credits?${params.toString()}`,
  );
}

export async function fetchCompleteArticleCredits(articleGroupKey: string): Promise<PriceHistoryResponse> {
  const pageSize = 2000;
  const first = await fetchArticleCredits(articleGroupKey, pageSize);
  const items = [...first.items];
  while (items.length < first.total) {
    const page = await fetchArticleCredits(articleGroupKey, pageSize, items.length);
    if (!page.items.length) throw new Error("Gutschriften konnten nicht vollständig geladen werden.");
    items.push(...page.items);
  }
  return { ...first, items, limit: items.length, offset: 0 };
}

// --- Preisanfrage / geteilter Referenzpreis-Lookup (#24, #23) ---

export type ReferencePriceBasis =
  | "purchasing_last"
  | "purchasing_best"
  | "best_3m"
  | "best_6m"
  | "best_12m"
  | "avg_12m"
  | "avg_24m";

export type ReferencePriceMode = "best" | "avg" | "last";

export type ArticleResolveCandidate = {
  article_group_key: string;
  primary_article_code: string | null;
  description_example: string | null;
  supplier_list: string | null;
  last_net_unit_price: string | number | null;
};

export type ArticleResolveItem = {
  input: string;
  normalized_code: string | null;
  status: "matched" | "ambiguous" | "unmatched";
  article_group_key: string | null;
  candidates: ArticleResolveCandidate[];
};

export type ArticleResolveResponse = {
  items: ArticleResolveItem[];
};

// Löst eingefügte Artikelnummern (Copy/Paste) auf Artikelgruppen auf.
// Read-only Lookup trotz POST — der Body trägt nur die Code-Liste.
export async function resolveArticleCodes(
  codes: string[],
): Promise<ArticleResolveResponse> {
  return fetchJson<ArticleResolveResponse>("/api/articles/resolve", {
    method: "POST",
    body: JSON.stringify({ codes }),
  });
}

export type ArticleReferencePrice = {
  reference_warning?: string | null;
  reference_uom?: string | null;
  reference_line_pos?: string | null;
  article_group_key: string;
  primary_article_code: string | null;
  description_example: string | null;
  product_family: string | null;
  brand_name: string | null;
  supplier_list: string | null;
  last_net_unit_price: string | number | null;
  last_invoice_doc_date: string | null;
  reference_price: string | number | null;
  reference_doc_date: string | null;
  reference_invoice_doc_id: string | null;
  reference_doc_number: string | null;
  reference_supplier: string | null;
  reference_source_kind: string | null;
  priced_line_count_window: number;
};

export type ArticleReferencePricesResponse = {
  basis: ReferencePriceBasis;
  mode: ReferencePriceMode;
  window_months: number;
  items: ArticleReferencePrice[];
  missing_article_group_keys: string[];
};

export function articleReferencePricesQueryKey(
  articleGroupKeys: string[],
  basis: ReferencePriceBasis,
) {
  return [
    "articles",
    "reference-prices",
    basis,
    [...articleGroupKeys].sort().join("|"),
  ] as const;
}

// Bestpreis je Artikelgruppe im Fenster der Basis, mit Belegnachweis.
export async function fetchArticleReferencePrices(
  articleGroupKeys: string[],
  basis: ReferencePriceBasis,
  context?: { suppliers: Record<string, string>; asOf: string },
): Promise<ArticleReferencePricesResponse> {
  return fetchJson<ArticleReferencePricesResponse>(
    basis.startsWith("purchasing_") ? "/api/purchasing-target/references" : "/api/articles/reference-prices",
    {
      method: "POST",
      body: JSON.stringify(basis.startsWith("purchasing_") ? { article_group_keys: articleGroupKeys, basis: basis.replace("purchasing_", ""), suppliers: context?.suppliers ?? {}, as_of: context?.asOf ?? new Date().toISOString().slice(0, 10) } : { article_group_keys: articleGroupKeys, basis }),
    },
  );
}
